import React from 'react';

import { makeClassNames } from '../../../utils/styles';
import PlayerCardBadge from '../../atoms/player-card-badge/player-card-badge';
import PlayerTeam from '../../atoms/player-team/player-team';
import PlayerCardFooter from '../../atoms/player-card-footer/player-card-footer';
import PlayerCardWrapper from '../player-card-wrapper/player-card-wrapper';
import { playerCardtypes } from '../../../typings/player';
import Text from '../../atoms/text/text';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Cta from '../../atoms/cta/cta';

import PlayerCardLegacy from './player-card-legacy';

import './player-card.scss';

export interface IPlayerCard {
  type: playerCardtypes;
  imageUrl?: string;
  firstName?: string;
  lastName: string;
  playerUrl?: string;
  mvp?: boolean;
  playerTeamImageUrl: string;
  playerTeamName: string;
  playerTeamColor?: string;
  playerPoints?: number;
  playerTeamShowPoints: boolean;
  playerTeamRole?: string;
  playerPosition?: number;
  playerTeamUrl?: string;
  label?: string;
  legacy?: boolean;
  lazy?: boolean;
  trackingDataValues?: {
    playerCard?: string;
    playerTeam?: string;
  };
}

const classNames = () => {
  return makeClassNames({
    player: 'player-card__player',
    playerHeaderLeft: ['player-card__header', 'player-card__header--left'],
    playerHeaderRight: ['player-card__header', 'player-card__header--right'],
    image: 'player-card__image',
    wrapper: 'player-card__wrapper',
    fullLink: 'player-card__full-link',
    playerCardPosition: 'player-card-position',
    playerCardPositionLabel: 'player-card-position__label',
  });
};

const PlayerCardPosition = (props: { position: number; className: string; labelClassName: string }) => {
  const { position, className, labelClassName } = props;

  return (
    <div className={className}>
      <Text tag="span" fontWeight="700" fontSize="16" color="light" className={labelClassName}>
        #{position}
      </Text>
    </div>
  );
};

const PlayerCard = (props: IPlayerCard) => {
  const {
    type,
    imageUrl,
    firstName,
    lastName,
    playerUrl,
    mvp = false,
    playerTeamImageUrl,
    playerTeamName,
    playerTeamColor = '#d6d6d6',
    playerPoints,
    playerPosition,
    playerTeamShowPoints,
    playerTeamRole,
    playerTeamUrl,
    label = '',
    legacy = false,
    lazy = false,
    trackingDataValues,
  } = props;

  const cn = classNames();

  const playerName = firstName ? `${firstName} ${lastName}` : lastName;

  const sizes = { '1x': 220, '2x': 440 };

  const placeholderUrl =
    'https://static.sky.it/images/skysport/configurations/placeholders/football/SkySpport-player_placeholder.png';

  const playerImage = (
    <ImgPlaceholder
      src={lazy ? undefined : imageUrl}
      data-src={lazy ? imageUrl : undefined}
      placeholder={placeholderUrl}
      alt={playerName}
      title={playerName}
      sizes={sizes}
    />
  );

  if (legacy) {
    return <PlayerCardLegacy />;
  }

  return (
    <PlayerCardWrapper playerTeamColor={playerTeamColor} hasHeadShot>
      <div className={cn.playerHeaderLeft}>
        {mvp ? <PlayerCardBadge label="mvp" /> : null}
        {playerPosition && type === playerCardtypes.MARCATORI ? (
          <PlayerCardPosition
            position={playerPosition}
            className={cn.playerCardPosition}
            labelClassName={cn.playerCardPositionLabel}
          />
        ) : null}
      </div>
      <div className={cn.playerHeaderRight}>
        <PlayerTeam
          imageUrl={playerTeamImageUrl}
          teamName={playerTeamName}
          teamUrl={playerCardtypes.MARCATORI ? playerTeamUrl : undefined}
          lazy={lazy}
          trackingDataValue={trackingDataValues?.playerTeam || undefined}
        />
      </div>
      <div className={cn.player}>
        {playerUrl ? <div className={cn.image}>{playerImage}</div> : <div className={cn.image}>{playerImage}</div>}
        <PlayerCardFooter
          type={type}
          firstName={firstName}
          lastName={lastName}
          playerUrl={playerUrl}
          label={label}
          points={playerPoints}
          playerTeamShowPoints={playerTeamShowPoints}
          playerTeamRole={playerTeamRole}
          playerTeamName={type === playerCardtypes?.MARCATORI ? playerTeamName : undefined}
          bgColor={type === playerCardtypes?.MARCATORI ? 'blue' : 'light'}
        />
      </div>
      {playerUrl ? (
        <Cta
          href={playerUrl}
          title={playerName}
          aria-label={playerName}
          className={cn.fullLink}
          trackingData={trackingDataValues?.playerCard ? { intcmp: trackingDataValues.playerCard } : undefined}
        />
      ) : null}
    </PlayerCardWrapper>
  );
};

export default PlayerCard;
